/**
 * Enhanced Error Handling System
 * Provides improved error handling with better context and user feedback
 */

import { logger } from './logger';
import { AppError, ErrorHandler } from './errorHandler';
import { trackReferentError, trackAuthError, trackDatabaseError, trackNetworkError } from './monitoringService';

// Enhanced error context interface
export interface EnhancedErrorContext {
  component?: string;
  function?: string;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  timestamp?: string;
  token?: string; // Sanitized token (first 8 chars + ...)
  referentId?: string;
  campaignId?: string;
  attempt?: number;
  maxAttempts?: number;
  [key: string]: any;
}

// Error severity levels
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

// Enhanced error class with severity and recovery suggestions
export class EnhancedAppError extends AppError {
  public readonly severity: ErrorSeverity;
  public readonly recoveryActions: string[];
  public readonly errorId: string;
  public readonly enhancedContext: EnhancedErrorContext;

  constructor(
    message: string,
    code: string,
    statusCode: number = 500,
    severity: ErrorSeverity = 'medium',
    recoveryActions: string[] = [],
    context?: EnhancedErrorContext
  ) {
    super(message, code, statusCode, true, context);
    this.severity = severity;
    this.recoveryActions = recoveryActions;
    this.errorId = this.generateErrorId();
    this.enhancedContext = this.enhanceContext(context);
  }

  private generateErrorId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `err_${timestamp}_${random}`;
  }

  private enhanceContext(context?: EnhancedErrorContext): EnhancedErrorContext {
    const baseContext: EnhancedErrorContext = {
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      ...context
    };

    // Sanitize sensitive data
    if (baseContext.token && baseContext.token.length > 8) {
      baseContext.token = baseContext.token.substring(0, 8) + '...';
    }

    return baseContext;
  }
}

// Enhanced error handler with better categorization and recovery
export class EnhancedErrorHandler extends ErrorHandler {
  /**
   * Handle errors with enhanced context and recovery suggestions
   */
  static handleEnhanced(
    error: Error | AppError,
    context?: EnhancedErrorContext
  ): EnhancedAppError {
    // If it's already an EnhancedAppError, just log and return
    if (error instanceof EnhancedAppError) {
      this.logEnhancedError(error);
      return error;
    }

    // If it's a regular AppError, enhance it
    if (error instanceof AppError) {
      const enhancedError = new EnhancedAppError(
        error.message,
        error.code,
        error.statusCode,
        this.determineSeverity(error),
        this.getRecoveryActions(error),
        context
      );
      this.logEnhancedError(enhancedError);
      return enhancedError;
    }

    // Handle specific error types with enhanced information
    const enhancedError = this.categorizeAndEnhance(error, context);
    this.logEnhancedError(enhancedError);
    return enhancedError;
  }

  /**
   * Categorize and enhance regular errors
   */
  private static categorizeAndEnhance(
    error: Error,
    context?: EnhancedErrorContext
  ): EnhancedAppError {
    const errorMessage = error.message?.toLowerCase() || '';

    // Network errors
    if (this.isNetworkError(error) || errorMessage.includes('network')) {
      return new EnhancedAppError(
        'Problème de connexion réseau',
        'NETWORK_ERROR',
        0,
        'high',
        [
          'Vérifiez votre connexion internet',
          'Réessayez dans quelques instants',
          'Contactez le support si le problème persiste'
        ],
        context
      );
    }

    // Token/Authentication errors
    if (errorMessage.includes('token') || errorMessage.includes('authentification')) {
      return new EnhancedAppError(
        'Problème d\'authentification',
        'AUTH_ERROR',
        401,
        'high',
        [
          'Vérifiez que le lien est correct',
          'Demandez un nouveau lien si nécessaire',
          'Contactez l\'expéditeur du lien'
        ],
        context
      );
    }

    // Validation errors
    if (errorMessage.includes('validation') || errorMessage.includes('invalid')) {
      return new EnhancedAppError(
        'Données invalides',
        'VALIDATION_ERROR',
        400,
        'medium',
        [
          'Vérifiez les informations saisies',
          'Assurez-vous que tous les champs requis sont remplis',
          'Contactez le support si le problème persiste'
        ],
        context
      );
    }

    // Database/Supabase errors
    if (this.isSupabaseError(error)) {
      return new EnhancedAppError(
        this.getSupabaseErrorMessage(error),
        'DATABASE_ERROR',
        500,
        'high',
        [
          'Réessayez dans quelques instants',
          'Vérifiez votre connexion',
          'Contactez le support si le problème persiste'
        ],
        context
      );
    }

    // Generic error
    return new EnhancedAppError(
      error.message || 'Une erreur inattendue s\'est produite',
      'UNKNOWN_ERROR',
      500,
      'medium',
      [
        'Rafraîchissez la page',
        'Réessayez votre action',
        'Contactez le support si le problème persiste'
      ],
      context
    );
  }

  /**
   * Determine error severity based on error type
   */
  private static determineSeverity(error: AppError): ErrorSeverity {
    switch (error.code) {
      case 'VALIDATION_ERROR':
        return 'medium';
      case 'AUTHENTICATION_ERROR':
      case 'AUTHORIZATION_ERROR':
        return 'high';
      case 'NETWORK_ERROR':
      case 'SUPABASE_ERROR':
        return 'high';
      case 'NOT_FOUND_ERROR':
        return 'medium';
      case 'CONFLICT_ERROR':
        return 'low';
      default:
        return 'medium';
    }
  }

  /**
   * Get recovery actions based on error type
   */
  private static getRecoveryActions(error: AppError): string[] {
    switch (error.code) {
      case 'VALIDATION_ERROR':
        return [
          'Vérifiez les informations saisies',
          'Assurez-vous que tous les champs requis sont remplis'
        ];
      case 'AUTHENTICATION_ERROR':
        return [
          'Vérifiez que le lien est correct',
          'Demandez un nouveau lien si nécessaire'
        ];
      case 'AUTHORIZATION_ERROR':
        return [
          'Contactez l\'administrateur',
          'Vérifiez vos permissions'
        ];
      case 'NETWORK_ERROR':
        return [
          'Vérifiez votre connexion internet',
          'Réessayez dans quelques instants'
        ];
      case 'NOT_FOUND_ERROR':
        return [
          'Vérifiez que le lien est correct',
          'Contactez l\'expéditeur si le lien est expiré'
        ];
      default:
        return [
          'Rafraîchissez la page',
          'Réessayez votre action',
          'Contactez le support si le problème persiste'
        ];
    }
  }

  /**
   * Log enhanced error with full context
   */
  private static logEnhancedError(error: EnhancedAppError): void {
    const logContext = {
      errorId: error.errorId,
      code: error.code,
      severity: error.severity,
      statusCode: error.statusCode,
      recoveryActions: error.recoveryActions,
      ...error.enhancedContext
    };

    // Track error in monitoring service based on category
    const category = error.enhancedContext.category;
    
    switch (category) {
      case 'referent_system':
        trackReferentError({
          message: error.message,
          level: 'error',
          referentId: error.enhancedContext.referentId,
          campaignId: error.enhancedContext.campaignId,
          token: error.enhancedContext.token,
          step: error.enhancedContext.step,
          context: logContext,
        });
        break;
        
      case 'authentication':
        trackAuthError({
          message: error.message,
          level: 'error',
          authMethod: error.enhancedContext.authMethod,
          userId: error.enhancedContext.userId,
          context: logContext,
        });
        break;
        
      case 'database':
        trackDatabaseError({
          message: error.message,
          level: 'error',
          query: error.enhancedContext.query,
          table: error.enhancedContext.table,
          operation: error.enhancedContext.operation,
          context: logContext,
        });
        break;
        
      case 'network':
        trackNetworkError({
          message: error.message,
          level: 'error',
          url: error.enhancedContext.url,
          method: error.enhancedContext.method,
          statusCode: error.statusCode,
          responseTime: error.enhancedContext.responseTime,
          context: logContext,
        });
        break;
        
      default:
        // Track as general error
        trackReferentError({
          message: error.message,
          level: 'error',
          context: logContext,
        });
    }

    switch (error.severity) {
      case 'critical':
        logger.error(`🚨 CRITICAL ERROR: ${error.message}`, logContext, error);
        break;
      case 'high':
        logger.error(`🔴 HIGH SEVERITY: ${error.message}`, logContext, error);
        break;
      case 'medium':
        logger.warn(`🟡 MEDIUM SEVERITY: ${error.message}`, logContext);
        break;
      case 'low':
        logger.info(`🟢 LOW SEVERITY: ${error.message}`, logContext);
        break;
    }
  }

  /**
   * Get user-friendly error message with recovery actions
   */
  static getEnhancedUserMessage(error: EnhancedAppError): {
    title: string;
    message: string;
    actions: string[];
    severity: ErrorSeverity;
  } {
    const baseMessage = this.getUserMessage(error);
    
    return {
      title: this.getErrorTitle(error),
      message: baseMessage,
      actions: error.recoveryActions,
      severity: error.severity
    };
  }

  /**
   * Get appropriate error title based on severity and type
   */
  private static getErrorTitle(error: EnhancedAppError): string {
    switch (error.severity) {
      case 'critical':
        return 'Erreur critique';
      case 'high':
        return 'Erreur importante';
      case 'medium':
        return 'Erreur';
      case 'low':
        return 'Avertissement';
      default:
        return 'Erreur';
    }
  }

  /**
   * Get toast variant based on error severity
   */
  static getToastVariant(error: EnhancedAppError): 'default' | 'destructive' {
    return error.severity === 'low' ? 'default' : 'destructive';
  }
}

// Utility function for React components
export const useEnhancedErrorHandler = () => {
  const handleError = (error: Error | AppError, context?: EnhancedErrorContext) => {
    return EnhancedErrorHandler.handleEnhanced(error, context);
  };

  const getEnhancedUserMessage = (error: EnhancedAppError) => {
    return EnhancedErrorHandler.getEnhancedUserMessage(error);
  };

  const getToastVariant = (error: EnhancedAppError) => {
    return EnhancedErrorHandler.getToastVariant(error);
  };

  return {
    handleError,
    getEnhancedUserMessage,
    getToastVariant
  };
};

// Utility for creating enhanced error context
export const createErrorContext = (
  component: string,
  functionName: string,
  additionalContext?: Partial<EnhancedErrorContext>
): EnhancedErrorContext => {
  return {
    component,
    function: functionName,
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    ...additionalContext
  };
};